import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { HStack, View, Box, useTheme, Divider, IconButton, CloseIcon, VStack, useToast, CheckIcon } from 'native-base';

import { Button, CleanIcon, InfoCircleIcon, Text } from '@pimm/base';
import { AutoAssignByPosition } from '@pimm/services/lib/sms-workforce/services';
import { AutoAssignByPositionRequest } from '@pimm/services/lib/sms-workforce';
import { formatToISOString, formatToLongDateTime, stringToDateLocal } from '@app/utils/date-formatter';
import { DayBlock, useSiteTime } from '@app/features/store-core';

type ConfirmQuickAssignCleaningProps = {
  dayBlock?: DayBlock;
  siteId?: string;
  subBlockTime?: string;
  onClose: (_confirm?: boolean) => void;
};

export const ConfirmQuickAssignCleaning = ({ dayBlock, siteId, subBlockTime, ..._props }: ConfirmQuickAssignCleaningProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const siteTime = useSiteTime();

  const scheduledTime = useMemo(() => {
    if (dayBlock?.startTime && subBlockTime) {
      const now = siteTime.today();
      const flooredNow = new Date(Math.floor(now.getTime() / (5 * 60 * 1000)) * (5 * 60 * 1000));
      const blockStartTime = stringToDateLocal(subBlockTime) ?? dayBlock?.startTime;
      const scheduledTime = blockStartTime && blockStartTime > now ? blockStartTime : flooredNow;
      return scheduledTime;
    }
    return undefined;
  }, [dayBlock?.startTime, subBlockTime]);

  const autoAssignByPosition = useMutation({
    mutationFn: async (_payload: AutoAssignByPositionRequest) => {
      await AutoAssignByPosition(_payload);
    },
    onSuccess: () => {
      _props.onClose();
      toast.show({
        placement: 'bottom',
        duration: 4000,
        render: () => (
          <HStack alignItems="center" space={3} pl={3} pr={4} py={2.5} bg="white" rounded="md" shadow={2}>
            <Box rounded="full" alignItems="center" justifyContent="center" boxSize={8} bg="success.50">
              <Box rounded="full" alignItems="center" justifyContent="center" boxSize={6} bg="success.100">
                <CheckIcon size={4} color="success.700" />
              </Box>
            </Box>

            <VStack>
              <Text size="md" fontWeight={600} color="gray.800" lineHeight="sm">
                {t('positioning_plan:quick_assign_success_title')}
              </Text>
              <Text size="sm" fontWeight={500} color="gray.600" lineHeight="md">
                {t('positioning_plan:quick_assign_success_message')}
              </Text>
            </VStack>
          </HStack>
        ),
      });
    },
  });

  const handleConfirm = () => {
    if (!siteId || !scheduledTime) return;
    autoAssignByPosition.mutate({
      scheduledTime: formatToISOString(scheduledTime),
      siteId,
    });
  };

  return (
    <View p={1}>
      <HStack space={2} alignItems="center" mb={4}>
        <Box rounded="full" boxSize={9} alignItems="center" justifyContent="center" bg="blue.100">
          <CleanIcon size={5} color={colors.blue[700]} />
        </Box>
        <Box flex={1}>
          <Text color="black" fontWeight={600} size="lg" lineHeight="sm">
            {t('positioning_plan:quick_assign_title')}
          </Text>
        </Box>

        <IconButton
          variant="unstyled"
          boxSize={7}
          p={0}
          _hover={{ bg: 'gray.50' }}
          icon={<CloseIcon size="16px" color="gray.500" />}
          onPress={() => _props.onClose(false)}
        />
      </HStack>

      <VStack space={4}>
        <Text size="md" color="gray.600" fontWeight={500} lineHeight="md">
          {t('positioning_plan:quick_assign_message')}
        </Text>

        {!!scheduledTime && (
          <Text size="2xl" color="gray.700" fontWeight={600} lineHeight="sm" textAlign="center">
            {formatToLongDateTime(scheduledTime)}
          </Text>
        )}
      </VStack>

      <Divider my={5} />

      {autoAssignByPosition.isError && (
        <HStack
          space={3}
          alignItems="center"
          mt={-2}
          mb={3}
          py={2.5}
          px={3}
          rounded="lg"
          borderWidth={1}
          borderColor="error.200"
          bg="error.25"
        >
          <Box rounded="full" alignItems="center" justifyContent="center" boxSize={8} bg="error.50">
            <Box rounded="full" alignItems="center" justifyContent="center" boxSize={6} bg="error.100">
              <InfoCircleIcon size={5} color="error.600" />
            </Box>
          </Box>

          <VStack>
            <Text size="md" fontWeight={600} color="gray.800" lineHeight="sm">
              {t('positioning_plan:quick_assign_error_title')}
            </Text>
            <Text size="sm" fontWeight={500} color="gray.600" lineHeight="md">
              {t('positioning_plan:quick_assign_error_message')}
            </Text>
          </VStack>
        </HStack>
      )}

      <HStack space={2}>
        <Button variant="unstyled" shadow={0} flex={1} bg="white" onPress={() => _props.onClose(false)}>
          {t('positioning_plan:cancel')}
        </Button>

        <Button
          flex={1}
          py={0}
          shadow={0}
          borderColor="blue.500"
          bg="blue.500"
          isLoading={autoAssignByPosition.isLoading}
          _text={{ color: 'white' }}
          _hover={{ borderColor: 'blue.600', bg: 'blue.600' }}
          _pressed={{ borderColor: 'blue.600', bg: 'blue.600' }}
          _spinner={{ color: 'white' }}
          onPress={handleConfirm}
        >
          {t('positioning_plan:quick_assign')}
        </Button>
      </HStack>
    </View>
  );
};
